import { gql } from '@apollo/client'
import {
  AI_ARTWORK_LIST_FIELDS,
  AI_MAGIC_FIELDS,
  DEFAULT_STYLE_DECORATION_OPTION_FIELDS,
} from './fragments'

const MODEL_CONFIG_FRAGMENT = gql`
  fragment MODEL_CONFIG_FRAGMENT on AiArtworkModelThemeOption {
    key
    name
    url
    isNew
    modelCode
    resolutionKey
    cfg
    samplerIndex
    ensd
    clipSkip
    modelVersion
    onlyMember
    whiteListModel
    modelDocumentUrl
    tagModelLinkId
    tag
    generateNum
    sdxl
    resolutionDefaultSize
    cutdiffProportion
    cfgScale1
    cfgScale2
    flux {
      chaos
      style
    }
  }
`

export const GET_AI_MENU = gql`
  ${DEFAULT_STYLE_DECORATION_OPTION_FIELDS}
  ${MODEL_CONFIG_FRAGMENT}
  query getAIMenu(
    $model: AiCreateArtworkModel = ANIME_DIFFUSION
    $pattern: AiCreateArtworkPattern
    $isTransparent: Boolean = false
  ) {
    pictureGraph {
      aiCreateArtworkOptionMenu(param: { pattern: $pattern }) {
        defaultArtWorkList
        numCeiling
        # 模型主题
        sdModelThemeOptions: modelThemeOptions(model: STABLE_DIFFUSION) {
          ...MODEL_CONFIG_FRAGMENT
          defaultSelectStyleDecorationOption {
            ...defaultStyleDecorationOptionFields
          }
          fourKDefault {
            ...MODEL_CONFIG_FRAGMENT
            defaultSelectStyleDecorationOption {
              ...defaultStyleDecorationOptionFields
            }
          }
        }
        adModelThemeOptions: modelThemeOptions(model: ANIME_DIFFUSION) {
          ...MODEL_CONFIG_FRAGMENT
          defaultSelectStyleDecorationOption {
            ...defaultStyleDecorationOptionFields
          }
          fourKDefault {
            ...MODEL_CONFIG_FRAGMENT
            defaultSelectStyleDecorationOption {
              ...defaultStyleDecorationOptionFields
            }
          }
        }
        mjModelThemeOptions: modelThemeOptions(model: MIDJOURNEY) {
          ...MODEL_CONFIG_FRAGMENT
          defaultSelectStyleDecorationOption {
            ...defaultStyleDecorationOptionFields
          }
          fourKDefault {
            ...MODEL_CONFIG_FRAGMENT
            defaultSelectStyleDecorationOption {
              ...defaultStyleDecorationOptionFields
            }
          }
        }
        # 透明图支持的模型
        layerDiffusionModelThemeOption @include(if: $isTransparent) {
          ...MODEL_CONFIG_FRAGMENT
          defaultSelectStyleDecorationOption {
            ...defaultStyleDecorationOptionFields
          }
          fourKDefault {
            ...MODEL_CONFIG_FRAGMENT
            defaultSelectStyleDecorationOption {
              ...defaultStyleDecorationOptionFields
            }
          }
        }
        # lab 支持的mj模型
        mjPatternModelThemeOptions: modelThemeOptions(model: PRETEND_MJ)
          @skip(if: $isTransparent) {
          ...MODEL_CONFIG_FRAGMENT
          defaultSelectStyleDecorationOption {
            ...defaultStyleDecorationOptionFields
          }
          fourKDefault {
            ...MODEL_CONFIG_FRAGMENT
            defaultSelectStyleDecorationOption {
              ...defaultStyleDecorationOptionFields
            }
          }
        }
        # 新的标签选择-包含风格修饰、元素魔法、艺术家
        sdStyleDecorationOptions: styleDecorationOptions(
          model: STABLE_DIFFUSION
        ) {
          key
          name
          type
          category
          choiceKey
        }
        adStyleDecorationOptions: styleDecorationOptions(
          model: ANIME_DIFFUSION
        ) {
          key
          name
          type
          category
          choiceKey
        }
        mjStyleDecorationOptions: styleDecorationOptions(modelCode: 301) {
          key
          name
          type
          category
          choiceKey
        }
        njStyleDecorationOptions: styleDecorationOptions(modelCode: 302) {
          key
          name
          type
          category
          choiceKey
        }
        mjv6StyleDecorationOptions: styleDecorationOptions(modelCode: 303) {
          key
          name
          type
          category
          choiceKey
        }
        njv6StyleDecorationOptions: styleDecorationOptions(modelCode: 304) {
          key
          name
          type
          category
          choiceKey
        }
        mjv61StyleDecorationOptions: styleDecorationOptions(modelCode: 305) {
          key
          name
          type
          category
          choiceKey
        }
        mjv7StyleDecorationOptions: styleDecorationOptions(modelCode: 306) {
          key
          name
          type
          category
          choiceKey
        }
        # 角色同人
        characterOptions: characterOptionsNew {
          key
          name
          type
          category
          isNew
          supportModelVersions
        }
        # Lora模型
        modelFusionOptions {
          key
          name
          type
          category
          weight: recommendedWeight
          isNew
          supportModelVersions
        }
        recommendedModelFusionOptions {
          key
          name
          type
          category
          weight: recommendedWeight
          isNew
          supportModelVersions
        }
        allSizeOptions: allResolutionInfo {
          resolutionKey
          sizeSetOptions {
            name
            desc
            url
            resolutionList {
              length
              width
              price
              membersPrice
              superType
              # 该尺寸是否支持潮汐模式
              supportTidal
            }
          }
        }
        # 描述提示词选项
        promptTipsOptions(num: 2, model: $model) {
          name
        }

        samplerOptions(model: $model) {
          key: samplerIndex
          name: samplerModelName
          modelVersion
        }
        controlPrefineOption {
          superType
          price
          membersPrice
        }
        styleModelOptions {
          modelCode
          name
          type
          url
          key
          isNew
          category
          collectsCount
          collectsCountStr
          collected
          type
          resolutionKey
          tagModelLinkId
          modelForVip
        }
        recommendedStyleModelOptions {
          modelCode
          name
          type
          url
          key
          isNew
          category
          collectsCount
          collectsCountStr
          collected
          type
          resolutionKey
          tagModelLinkId
          modelForVip
        }
        # 我的融合模型
        userTrainedModelOptions {
          key: id
          name: modelName
          weight: defaultWeight
          layerNumber
          baseModelOption {
            baseModelType
            name
          }
        }
      }
    }
  }
`

/** 收藏的风格模型列表 */
export const GET_COLLECTED_STYLE_MODEL_LIST = gql`
  query getCollectedStyleModelList {
    pictureSocialQuery {
      collectedStyleModelList {
        modelCode
        name
        type
        url
        key
        isNew
        category
        collectsCount
        collectsCountStr
        collected
        resolutionKey
        tagModelLinkId
        modelForVip
      }
    }
  }
`

/** 模型标签 */
export const GET_MODEL_TAGS = gql`
  query getModelTags($modelCode: Int) {
    pictureGraph {
      aiCreateArtworkOptionMenu {
        styleModelTagOptions(modelCode: $modelCode) {
          key
          name
          type
          category
          choiceKey
        }
      }
    }
  }
`

/** 收藏风格模型 */
export const COLLECT_STYLE_MODEL = gql`
  mutation collectStyleModel($param: GeneralSocialActionInput!) {
    pictureSocialMutation {
      status: collectModel(param: $param)
    }
  }
`

/** 用户权益卡 */
export const GET_AI_MEMBER_LIST = gql`
  query getAIMemberList(
    $statusList: [CardStatus] = [EFFECTIVE]
    $groupList: [AccountGroupTypeEnum]
  ) {
    accountModule {
      userMemberCardList(
        param: { statusList: $statusList, groupList: $groupList }
      ) {
        cardType
        name
        image
        price
        showPrice
        hold
        endTime
        holdCardInfo {
          num
          endTime
          rightType
        }
        cardRuleInfo
        cardRight {
          icon
          num
          name
          rightType
        }
      }
    }
  }
`

/** 所有模型支持的尺寸 */
export const GET_AI_SIZES = gql`
  query getAISizes {
    pictureGraph {
      aiCreateArtworkOptionMenu {
        size: sizeOptions {
          name
        }
      }
    }
  }
`

/** 描述提示词选项 */
export const GET_AI_PROMPTS = gql`
  query getAIPrompt($model: AiCreateArtworkModel!, $num: Int = 2) {
    pictureGraph {
      aiCreateArtworkOptionMenu {
        promptTipsOptions(num: $num, model: $model) {
          name
        }
      }
    }
  }
`

/** 作画时间、可用作画次数等 */
export const GET_AI_TOTAL_INFO = gql`
  query getAITotalInfo($model: AiCreateArtworkModel = ANIME_DIFFUSION) {
    pictureGraph {
      aiArtworkTotalInfo {
        expectedSeconds: expectedSeconds(model: $model) {
          queueNum
          freeExpectedSeconds
          proExpectedSeconds
          reduceTime
          tidalQueueOverloaded
        }
        userIntegral
      }
    }
  }
`

/** 单一模型的作画时长 */
export const GET_EXPECTED_SECONDS = gql`
  query getExpectedSeconds($modelCode: Int!) {
    pictureGraph {
      aiArtworkTotalInfo {
        expectedSeconds: expectedSecondsByModelCode(modelCode: $modelCode) {
          queueNum
          freeExpectedSeconds
          proExpectedSeconds
          reduceTime
          tidalQueueOverloaded
        }
      }
    }
  }
`

/** 生成作画 */
export const AI_CREATE_ARTWORK = gql`
  mutation aiCreateArtwork($param: AiCreateArtworkParam!) {
    pictureMutation {
      id: aiCreateArtwork(param: $param)
    }
  }
`

/** 作画预处理 */
export const AI_PRE_CREATE = gql`
  query aiPreCreate($param: AiCreateArtworkParam!) {
    pictureGraph {
      aiArtworkPayStrategyTips(param: $param) {
        integralUse {
          integralUseOnAccelerate
          integralUseOnCreate
          integralUseOnResolution
          discountIntegral
        }
        userRightsUse {
          dailyFreeCreateTimesUse
          deservedCreateTimesUse
        }
        vipRightsUse {
          accelerateTimesUse
          createTimesUse
        }
      }
    }
  }
`

export const GET_AI_ARTWORK_PAGE = gql`
  ${AI_ARTWORK_LIST_FIELDS}
  query getAIArtWorkPage(
    $first: Int
    $offset: Int
    $param: AiArtworkQueryInput
    # 精简广场/投稿选图的字段
    $simplify: Boolean = false
    $plaza: Boolean = false
  ) {
    pictureGraph {
      aiArtworkPage(page: { first: $first, offset: $offset }, param: $param) {
        edges {
          node {
            ...aiArtworkListFields
          }
        }
        pageInfo {
          totalCount
          hasNextPage
        }
      }
    }
  }
`

/** 关联同一批的创作 */
export const GET_BATCH_AI_ARTWORK = gql`
  query getAatchAiArtwork($id: Long) {
    pictureGraph {
      batchAiArtwork(id: $id) {
        id
        extend {
          squarePostLink
          backImagePostId
        }
      }
    }
  }
`

export const GET_AI_LAB_ARTWORK_PAGE = gql`
  ${AI_ARTWORK_LIST_FIELDS}
  query getAILabArtWorkPage(
    $first: Int
    $offset: Int
    $param: AiArtworkQueryInput
    # 精简广场/投稿选图的字段
    $simplify: Boolean = false
    $plaza: Boolean = false
  ) {
    pictureGraph {
      aiLabArtworkPage(
        page: { first: $first, offset: $offset }
        param: $param
      ) {
        edges {
          node {
            ...aiArtworkListFields
          }
        }
        pageInfo {
          totalCount
          hasNextPage
        }
      }
    }
  }
`

export const GET_AI_ARTWORK = gql`
  query getAIArtWork($id: Long!, $collectionId: Long) {
    pictureGraph {
      aiArtwork(id: $id, aiCollectionId: $collectionId) {
        id
        beforeAfterId {
          beforeId
          afterId
        }
        # ai大赛作品
        artwork {
          id
          status
        }
        modelTheme
        styleDecorationOptions {
          key
          name
          type
        }
        styleModelOption {
          key
          name
          collected
          onShelf
        }
        characterOptions {
          key
          name
          type
        }
        modelFusionOptions: modelFusion {
          key
          name
          weight
        }
        userTrainedModelOption {
          weight
          trainedModel {
            id
            modelName
            valid
          }
        }

        artWorkUrl
        miniArtWorkUrl
        completeTime
        expectedSeconds
        completePercent
        queuePercent
        generateTime
        status
        initImageUrl
        multiReferenceImageUrlList
        creativityDegree
        prompt
        size
        userName
        model
        specificSize
        width
        length
        involveYellow
        technologyInfo {
          electricity
          gpuType
          serverId
        }
        superSizePicInfo {
          artWorkUrl
          integral
          length
          multiple: multipleNew
          status
          width
        }
        riskControl {
          status
          reason
        }
        extend {
          similarAiArtworkPost {
            id
          }
          squarePostLink
          # 是否已返图
          backImagePostId
          # 是否参加大赛 已投稿
          competitionLink
          #大赛排名
          competitionRank
          originalSquarePost {
            id
            status
          }
          copyrightArtwork {
            id
            type
            status
          }
        }
        maskImageUrl
        maskZoneImageUrl
        modelCode
        resolutionKey
        ucPrompt
        pattern
        doRepairTheHand # 是否是修手后的图
        supportRepairTheHand # 生成该图的模型是否支持修手
        createSource # 创建来源 0-正常绘画 1-一键照改漫
        # 预处理图
        pretreatmentImage {
          url
        }
        prefineInfo {
          strength
          multiple
        }
        seed
        cfg
        ensd
        clipSkip
        # 预处理方式
        pretreatmentMethod
        superType
        samplerModelInfo {
          samplerModelName
          samplerIndex
          # steps
        }
        supportPrefine # 生成该图的模型是否支持精绘
        serviceStatus # 图片是否被清除（非会员超过1000张会被清理）
        violationRefundStatus # 违规退还状态
        # 浏览器者身份:OWNER 则代表是买断者身份
        viewUser
        queueType
        # 作品是否被收藏为自定义风格模型
        collectedStyleSet
        mjParam {
          chaos
          quality
          stylize
          tile
          upbeta
          srefUrls
          sw
          crefUrls
          cw
        }
        failMessage {
          failMessage
        }
        anyTextMode
        eta
        strength
        pattern
        layerDiffusion {
          endingStep
          weight
        }
        modelDefaultParam {
          cfg
          clipSkip
          ensd
          modelCode
          modelParamChangeTime
          samplerIndex
        }
        instantStyle {
          imageUrl
          controlWeight
          showType
          pulidMode
        }
        modelInfo {
          modelVersion
        }
        cutDiffusion {
          cfgScale1
          cfgScale2
          cutdiffProportion
        }
        flux {
          chaos
          style
        }
      }
    }
  }
`

export const POLL_AI_ARTWORK = gql`
  query pollAIArtWork($id: Long!) {
    pictureGraph {
      aiArtworkGeneratingInfo(id: $id) {
        id
        artWorkUrl
        miniArtWorkUrl: miniArtworkUrl
        expectedSeconds
        status
        completeTime
        completePercent
        queuePercent
        nextPollingTime
        queueBeforeNum
        reduceTime
        involveYellow
        toastMessage
      }
    }
  }
`

export const POLL_AI_ARTWORKS = gql`
  query pollAIArtWorks($ids: [Long]!) {
    pictureGraph {
      aiArtworksGeneratingInfo(ids: $ids) {
        list {
          id
          artWorkUrl
          expectedSeconds
          status
          completeTime
          completePercent
          queuePercent
          nextPollingTime
          queueBeforeNum
          reduceTime
          miniArtWorkUrl
          involveYellow
          specificSize
          superSizePicInfo {
            artWorkUrl
            integral
            length
            multiple: multipleNew
            status
            width
          }
          # 预处理图
          pretreatmentImage {
            url
          }
          # 预处理方式
          pretreatmentMethod
          pattern
        }
        nextPollingTime
      }
    }
  }
`

export const POLL_SUPER_AI_ARTWORK = gql`
  query pollSuperAiArtwork($id: Long!) {
    pictureGraph {
      aiArtwork(id: $id) {
        id
        superSizePicInfo {
          artWorkUrl
          integral
          length
          multiple: multipleNew
          status
          width
        }
      }
    }
  }
`

export const DELETE_AI_ARTWORK = gql`
  mutation deleteAiArtwork($ids: [ID!]!) {
    pictureMutation {
      nums: deleteAiArtwork(ids: $ids)
    }
  }
`

export const SUPER_SIZE_AI_ARTWORK = gql`
  mutation superSizeAiArtwork(
    $id: Long!
    $multiple: Int!
    $multipleNew: Float
    $payStrategy: AiArtworkPayStrategyInput
  ) {
    pictureMutation {
      status: superSizeAiArtwork(
        aiArtWorkId: $id
        multiple: $multiple
        multipleNew: $multipleNew
        payStrategy: $payStrategy
      )
    }
  }
`
/** 批量超分图片 */
export const SUPER_SIZE_AI_ARTWORKS = gql`
  mutation superSizeAiArtworks($input: SuperSizeAiArtworkInput!) {
    pictureMutation {
      status: superSizeAiArtworks(input: $input)
    }
  }
`

export const SPEED_ACCELERATE_AI_ARTWORK = gql`
  mutation aiCreateArtworkAccelerate($param: AiCreateArtworkAccelerate!) {
    pictureMutation {
      status: aiCreateArtworkAccelerate(param: $param)
    }
  }
`
export const CANCEL_CREATE_AI_ARTWORK = gql`
  mutation cancelAiCreateArtwork($id: Long!) {
    pictureMutation {
      status: cancelAiCreateArtwork(id: $id)
    }
  }
`

export const GET_AI_ARTWORK_NEWEST_36 = gql`
  query getAIArtWorkNewst36(
    $first: Int
    $offset: Int
    $param: AiArtworkQueryInput
  ) {
    pictureGraph {
      aiArtworkPage(page: { first: $first, offset: $offset }, param: $param) {
        edges {
          node {
            id
            artWorkUrl
            miniArtWorkUrl
            completePercent
            queuePercent
            completeTime
            expectedSeconds
            generateTime
            status
            initImageUrl
            multiReferenceImageUrlList
            creativityDegree
            prompt
            size
            style
            userName
            model
            specificSize
            involveYellow
            modelTheme
            chatGptOptimize
            styleDecorationOptions {
              key
              name
              type
            }
            styleModelOption {
              key
              name
            }
            characterOptions {
              key
              name
              type
            }
            modelFusionOptions: modelFusion {
              key
              name
              weight
            }
            userTrainedModelOption {
              weight
              trainedModel {
                id
                modelName
                valid
              }
            }
            superSizePicInfo {
              artWorkUrl
              integral
              length
              multiple: multipleNew
              status
              width
            }
            # ai大赛作品
            artwork {
              id
              status
            }
            width
            length
            maskImageUrl
            maskZoneImageUrl
            riskControl {
              status
            }
            modelCode
            resolutionKey
            extend {
              squarePostLink
              # 是否已返图
              backImagePostId
              similarAiArtworkPost {
                id
              }
              copyrightArtwork {
                id
                type
                status
              }
            }
            ucPrompt
            doRepairTheHand # 是否开启修手
            createSource # 创建来源 0-正常绘画 1-一键照改漫
            # 预处理图
            pretreatmentImage {
              url
            }
            seed
            cfg
            ensd
            clipSkip
            # 预处理方式
            pretreatmentMethod
            samplerModelInfo {
              samplerModelName
              samplerIndex
              # steps
            }
            superType
            serviceStatus # 图片是否被清除（非会员超过1000张会被清理）
            queueType
            violationRefundStatus # 违规退还状态
            mjParam {
              chaos
              quality
              stylize
              tile
              upbeta
              srefUrls
              sw
              crefUrls
              cw
            }
            failMessage {
              failMessage
            }
            anyTextMode
            eta
            strength
            pattern
            layerDiffusion {
              endingStep
              weight
            }
            modelDefaultParam {
              cfg
              clipSkip
              ensd
              modelCode
              modelParamChangeTime
              samplerIndex
            }
            instantStyle {
              imageUrl
              controlWeight
              showType
              pulidMode
            }
            cutDiffusion {
              cfgScale1
              cfgScale2
              cutdiffProportion
            }
            flux {
              chaos
              style
            }
          }
        }
        pageInfo {
          totalCount
          hasNextPage
        }
      }
    }
  }
`
/** 申诉理由 */
export const APPEAL_AI_ARTWORK = gql`
  mutation appealAiArtWork($param: AiArtworkAppealInput!) {
    pictureMutation {
      status: appealAiArtwork(input: $param)
    }
  }
`

/** 分享下载原图链接 */
export const SHARE_ARTWORK_URL = gql`
  query shareArtworkUrl($id: Long!) {
    pictureGraph {
      url: getAiArtworkUrlByInvitationCode(aiArtworkId: $id)
    }
  }
`

/** 分享作品详情 */
export const SHARE_ARTWORK_DETAIL = gql`
  query shareArtWorkDetail($id: Long!) {
    pictureGraph {
      aiArtwork: aiArtworkShare(id: $id) {
        id
        miniArtWorkUrl
        involveYellow
        riskControl {
          status
        }
        width: length
        height: width
        serviceStatus # 图片是否被清除（非会员超过1000张会被清理）
        violationRefundStatus # 违规退还状态
      }
    }
  }
`

/** 分享作者信息 */
export const ARTWORK_USERINFO = gql`
  query getArtworkUserInfo($uid: Long) {
    userinfo: copyrightUserInfo(uid: $uid) {
      avatarUrl
      nickname
      userRoleInfo {
        artist
        aiArtist
      }
      memberDetail {
        hold
      }
    }
  }
`

/** 修手 */
export const FIX_HAND = gql`
  mutation fixHand($id: Long!) {
    pictureMutation {
      id: aiCreateArtworkRepairTheHand(aiArtworkId: $id)
    }
  }
`
/** 查询作品是否能发词图，不能发的，建议用户发广场 */
export const AI_POST_CHECK = gql`
  query aiPostCheck($ids: [Long]) {
    squareModule {
      aiPostCheck(ids: $ids) {
        originalScore {
          promptSimilarScore
          originalScore
        }
        resCode
      }
    }
  }
`
/** 查询作品是否能发返图 */
export const PRE_POST_BACK_IMAGE = gql`
  query prePostBackImageQuery($aiArtworkId: Long) {
    squareModule {
      prePostBackImage(aiArtworkId: $aiArtworkId)
    }
  }
`

/** 一键精绘 */
export const PREFINE = gql`
  mutation prefine($id: Long!) {
    pictureMutation {
      id: aiCreateArtworkPrefine(aiArtworkId: $id)
    }
  }
`

/** controlnet根据图片精绘options */
export const GET_CONTROLNET_PREFINE_OPTIONS = gql`
  query getControlnetPrefineOptions($width: Int, $height: Int) {
    pictureGraph {
      aiCreateArtworkOptionMenu {
        controlPrefineOption(width: $width, height: $height) {
          superType
          price
          membersPrice
        }
      }
    }
  }
`

export const ChatGPT_Result = gql`
  query chatGptResult($taskId: String!) {
    chatGPTQuery {
      gptResult: asyncOptPromptResult(taskId: $taskId) {
        result
        status
        taskId
      }
    }
  }
`

/** 使用chatgpt生成描述 */
export const ChatGPT_Make = gql`
  mutation chatGptMake(
    $model: AiCreateArtworkModel
    $text: String
    $taskId: String!
    $language: Language
    $modelCode: Int
  ) {
    chatGPTMutation {
      content: asyncOptPrompt(
        model: $model
        original: $text
        taskId: $taskId
        language: $language
        modelCode: $modelCode
      ) {
        taskId
        timeSeconds
      }
    }
  }
`

/** 恢复（被清理的）ai作品 */
export const RESTORE_AI_ARTWORK = gql`
  mutation restoreAiArtwork($id: Long!) {
    pictureMutation {
      ids: restoreAiArtwork(aiArtworkId: $id)
    }
  }
`

/** AI作画推荐支付策略 */
export const GET_ARTWORK_PAY_STRATEGY = gql`
  query getArtworkPayStragegy($id: Long!) {
    pictureGraph {
      aiArtworkPayStrategyTipsToOne(aiArtworkId: $id) {
        integralUse {
          a: integralUseOnAccelerate
          b: integralUseOnCreate
          c: integralUseOnResolution
          d: integralUseOnSuperSize
        }
      }
    }
  }
`

/** 作品存储使用空间 */
export const GET_USAGE_OF_AIARTWORK = gql`
  query getUsageOfMyAiArtwork {
    pictureGraph {
      usageOfMyAiArtwork
    }
  }
`

/** 用ai作品id收藏风格模型 */
export const COLLECT_CUSTOM_STYLE = gql`
  mutation collectCustomStyle($id: Long!, $name: String!) {
    pictureMutation {
      collectCustomStyleSetByAiArtwork(aiArtworkId: $id, param: { name: $name })
    }
  }
`

/** 删除自定义的风格模型 */
export const DELETE_CUSTOM_STYLE = gql`
  mutation deleteCustomStyle($key: String!) {
    pictureMutation {
      status: deleteCustomStyleSets(keys: [$key])
    }
  }
`

/** 通过ai作品id删除自定义的风格模型 */
export const DELETE_CUSTOM_STYLE_BY_ARTWORK = gql`
  mutation deleteCustomStyleByArtwork($id: Long!) {
    pictureMutation {
      status: deleteCustomStyleSetByAiArtwork(aiArtworkId: $id)
    }
  }
`

/** 魔法骰子主题 */
export const GET_DICE_THEME = gql`
  query getDiceTheme {
    commonQuery {
      magicDiceThemeList {
        id
        name
        isNew
        model
        modelCode
      }
    }
  }
`

/** 魔法骰子查询 */
export const GET_DICE_RESULT = gql`
  ${AI_MAGIC_FIELDS}
  query getDiceResult(
    $param: MagicDiceSearchInput
    $onlyPrompt: Boolean = false
  ) {
    commonQuery {
      magicDiceSearch(input: $param) {
        ...aiMagicFields
      }
    }
  }
`

/** 智能魔法查询 */
export const GET_MIND_DICE_RESULT = gql`
  ${AI_MAGIC_FIELDS}
  query getMindDiceResult(
    $param: SmartMagicInput
    $onlyPrompt: Boolean = false
  ) {
    commonQuery {
      smartMagicSearch(input: $param) {
        ...aiMagicFields
      }
    }
  }
`

/** 图生图支持的模型 */
export const GET_IMG_TO_IMG_MODEL_OPTIONS = gql`
  ${DEFAULT_STYLE_DECORATION_OPTION_FIELDS}
  query GetImgToImgModelOptions {
    pictureGraph {
      aiCreateArtworkOptionMenu {
        # 模型主题
        sdModelThemeOptions: modelThemeOptions(model: STABLE_DIFFUSION) {
          key
          name
          url
          modelCode
          onlyMember
          category
          isNew
          tag
          defaultSelectStyleDecorationOption {
            ...defaultStyleDecorationOptionFields
          }
        }
        adModelThemeOptions: modelThemeOptions(model: ANIME_DIFFUSION) {
          key
          name
          url
          modelCode
          onlyMember
          category
          isNew
          tag
          defaultSelectStyleDecorationOption {
            ...defaultStyleDecorationOptionFields
          }
        }
        mjModelThemeOptions: modelThemeOptions(model: MIDJOURNEY) {
          key
          name
          url
          modelCode
          onlyMember
          category
          isNew
          tag
          defaultSelectStyleDecorationOption {
            ...defaultStyleDecorationOptionFields
          }
        }
      }
    }
  }
`

/** 作品详情页：查询作品Api参数 */
export const GET_ARTWORK_API = gql`
  query getArtworkApi($id: Long!) {
    aiProGraph {
      getArtworkApiParam(id: $id) {
        apiParam
        webuiParam
      }
    }
  }
`
